/**
 * Money, numbers and dates, written the way this portal's tenant writes them.
 *
 * There were 47 places that said `'en-US'` or `'USD'` by hand — one per
 * page, each its own little `formatCurrency`. Every one of them was correct
 * for the first MSP and wrong for the first MSP in Sydney, whose customers
 * would have seen their invoices in dollars that were not theirs. They all
 * route here now, and the locale and currency come from the branding the
 * resolve endpoint delivered before the app rendered (`brandingStore`).
 *
 * The contract that made the migration safe: with the defaults — en-US,
 * USD — every function returns byte for byte what the call it replaced
 * returned. `formatMoney(1234.5)` is `$1,234.50`, as
 * `new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })`
 * always was. `scripts/format-check.js` proves it outside the browser.
 *
 * Plain functions, not hooks, on purpose: they are called from helpers and
 * `.map()` callbacks as much as from render. That is fine because the
 * branding is resolved before the first render; a component that needs to
 * re-render on a *change* of branding (there is none today) would read the
 * store with `useBranding()` as well.
 */
// Explicit extension: this module also runs under plain node (scripts/format-check.js).
import { useBrandingStore, DEFAULT_LOCALE, DEFAULT_CURRENCY } from '../context/brandingStore.js';

export { DEFAULT_LOCALE, DEFAULT_CURRENCY };

/** The tenant's locale and currency right now. */
export function currentFormatSettings() {
  const b = useBrandingStore.getState().branding || {};
  return {
    locale: b.locale || DEFAULT_LOCALE,
    currency: b.currency || DEFAULT_CURRENCY,
  };
}

/** A number from whatever the API sent: a number, a numeric string, null. */
function toNumber(value) {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value);
  if (value === null || value === undefined) return 0;
  return Number(value);
}

/**
 * An amount of money: `$1,234.50`, `A$1,234.50`, `1.234,50 €`.
 *
 * `currency` and `locale` override the tenant's; any other Intl option
 * (`minimumFractionDigits`, `signDisplay`, …) is passed through.
 */
export function formatMoney(amount, { currency, locale, ...options } = {}) {
  const settings = currentFormatSettings();
  return new Intl.NumberFormat(locale || settings.locale, {
    style: 'currency',
    currency: currency || settings.currency,
    ...options,
  }).format(toNumber(amount));
}

/** A plain number in the tenant's locale: `1,234.5` or `1.234,5`. */
export function formatNumber(value, { locale, ...options } = {}) {
  return new Intl.NumberFormat(locale || currentFormatSettings().locale, options).format(toNumber(value));
}

const DATE_DEFAULT = { year: 'numeric', month: 'short', day: 'numeric' };
const DATETIME_DEFAULT = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };

function toDate(value) {
  if (value instanceof Date) return value;
  if (value === null || value === undefined || value === '') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * A calendar date: `Oct 15, 2026` in en-US, `15 Oct 2026` in en-AU.
 *
 * Takes the same options `toLocaleDateString` does. Returns '' for nothing
 * and for an unparseable value, so a missing date renders as blank rather
 * than "Invalid Date". Note that this formats an *instant*; for a date-only
 * string compared as a date, see `utils/dates.js`.
 */
export function formatDate(value, { locale, ...options } = {}) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString(locale || currentFormatSettings().locale, Object.keys(options).length ? options : DATE_DEFAULT);
}

/** A date and time: `Oct 15, 2026, 02:30 PM` in en-US. Same options as `toLocaleString`. */
export function formatDateTime(value, { locale, ...options } = {}) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(locale || currentFormatSettings().locale, Object.keys(options).length ? options : DATETIME_DEFAULT);
}

export default { formatMoney, formatNumber, formatDate, formatDateTime, currentFormatSettings };
